import * as R from 'remeda'
import { useEffect, useMemo, useState } from 'react'
import { ParsedBlock } from '../chat-data/types'

interface UseThrottledBlocksReturnType {
  isThrottling: boolean
  throttledBlocks: Array<ParsedBlock>
}

const PERCENTAGE_RANDOM_DEVIATION = 30
const MIN_TIMEOUT_MESSAGE_LENGTH = 10
const MIN_TIMEOUT = 1000
const MAX_TIMEOUT = 3000
const MAX_TIMEOUT_MESSAGE_LENGTH = 500

const getTimeout = (messageLength: number): number => {
  if (messageLength === 0) {
    return 0
  }
  /** We want to add some randomness to make it more natural */
  const randomDeviation =
    R.randomInteger(100 - PERCENTAGE_RANDOM_DEVIATION, 100 + PERCENTAGE_RANDOM_DEVIATION) / 100
  const ratio =
    (messageLength - MIN_TIMEOUT_MESSAGE_LENGTH) /
    (MAX_TIMEOUT_MESSAGE_LENGTH - MIN_TIMEOUT_MESSAGE_LENGTH)
  const result = R.clamp(MIN_TIMEOUT + ratio * (MAX_TIMEOUT - MIN_TIMEOUT), {
    min: MIN_TIMEOUT,
    max: MAX_TIMEOUT,
  })
  return result * randomDeviation
}

export const useThrottledBlocks = ({
  blocks,
}: {
  blocks: Array<ParsedBlock>
}): UseThrottledBlocksReturnType => {
  const [firstMessageId, setFirstMessageId] = useState<string | undefined>(blocks?.[0]?.id)
  const [initialEnd, setInitialEnd] = useState(blocks.length)
  const [end, setEnd] = useState(blocks.length)
  const [lastThrottledBlockAddedAt, setLastThrottledBlockAddedAt] = useState<Date | undefined>(
    undefined
  )

  if (firstMessageId !== blocks?.[0]?.id) {
    setInitialEnd(blocks.length)
    setEnd(blocks.length)
    setFirstMessageId(blocks?.[0]?.id)
    setLastThrottledBlockAddedAt(undefined)
  }

  const isWithoutPendingBlocks = blocks.slice(end).every(block => getMessageLength(block) === 0)

  if (end < blocks.length && (isWithoutPendingBlocks || end === 0 || initialEnd > end)) {
    setEnd(blocks.length)
  }

  const currentlyThrottledBlockType = blocks[end]?.type

  const messageLength = useMemo(() => {
    return getMessageLength(blocks[end])
  }, [blocks, end])

  const timeout = useMemo(() => {
    const now = Date.now()
    const expectedTimeout = getTimeout(messageLength)
    if (currentlyThrottledBlockType === 'PiperAiMessage') {
      const timeElapsed = now - (lastThrottledBlockAddedAt?.getTime() ?? 0)
      if (timeElapsed > expectedTimeout) {
        return 0
      }
      return expectedTimeout
    }
    return expectedTimeout
  }, [lastThrottledBlockAddedAt, messageLength, currentlyThrottledBlockType])

  useEffect(() => {
    if (blocks.length <= end) {
      return
    }
    const timeoutId = setTimeout(() => {
      setEnd(end + 1)
      setLastThrottledBlockAddedAt(new Date())
    }, timeout)
    return () => {
      clearTimeout(timeoutId)
    }
  }, [blocks.length, end, timeout])

  return {
    throttledBlocks: blocks.slice(0, end),
    isThrottling: blocks.length > end,
  }
}

const getMessageLength = (block: ParsedBlock | undefined) => {
  if (!block) {
    return 0
  }
  switch (block.type) {
    case 'PiperAiMessage':
    case 'Message':
      return R.sumBy(block.parts, part => {
        if (part.type === 'Text') {
          return part.text.length
        }
        return 100
      })
    case 'BookMeeting':
    case 'SuggestedTimes':
    case 'FillForm':
      return 300
    case 'LiveMessage':
    case 'ReplyButtons':
    case 'RouteToRep':
    case 'SystemMessage':
    case 'Error':
    case 'StatusChange':
    case 'BookedMeeting':
    case 'TerminateConversation':
    case 'EnterPiperAiChat':
    case 'MessageToPiperAi':
      return 0
    default:
      block satisfies never
      return 0
  }
}
